/* eslint-disable @typescript-eslint/member-ordering */
import {
    AddWidgetDialogInputData,
    DisplayAction,
    DisplayActionIcon,
    InstantiatedWidget,
    IqConfigurationStripped,
    Organization,
    User,
    Widget,
    WidgetSettingsDialogInputData,
} from '@newgenus/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { GraphClockService } from '../graph/graph-clock';
import { WidgetSettingsDialogComponent } from '../dialogs/widget-settings-dialog/widget-settings-dialog.component';
import { AddWidgetDialogComponent } from '../dialogs/add-widget-dialog/add-widget-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { graphSubtitle } from '../graph/graph-dates';
import {
    gridColumnsForWidth,
    heightInRows,
    legacyUnits,
    moveItem,
    rowHeightFor,
    widthInTwelfths,
    widthLabel,
} from './dashboard-layout';
import { LayoutGridItem } from './layout-grid/layout-grid.component';

/** Matches the grid gap in the stylesheet. */
const GRID_GAP = 12;

@Component({
    selector: 'shared-dashboard-grid',
    templateUrl: './dashboard-grid.component.html',
    styleUrls: ['./dashboard-grid.component.scss'],
    standalone: false,
})
export class DashboardGridComponent implements OnInit, OnDestroy, OnChanges {
    // public isHandset$!: Observable<any>;

    //#region Variables
    // Variables for this component that are within scope of "this" component.

    @Input() integrations: IqConfigurationStripped[] | undefined;
    @Input() organization: Organization | undefined;
    @Input() user: User | undefined;
    @Input() dashboardKey = '';
    @Input() widgets: InstantiatedWidget<any, any>[] = [];
    @Output() addNewWidget = new EventEmitter<Widget<any>>();
    @Output() updateWidgetsOrder = new EventEmitter<InstantiatedWidget<any, any>[]>();
    @Output() updateWidget = new EventEmitter<InstantiatedWidget<any, any>>();
    @Output() deleteWidget = new EventEmitter<InstantiatedWidget<any, any>>();

    /**
     * Based on the dashboardKey, it will just be formatted first.
     */
    public dashboardName = '';

    /**
     * Menu options for widgets.
     * This is dynamically generated on the fly for each widget.
     */
    public menuOptions: CustomMenuOptions<{ selected: boolean }>[] = [];

    public originalWidgets = [
        { title: 'Company 7R Graph', cols: 4, rows: 2, color: '#4a4a4a', component: 'company-7r-graph', expand: false },
        { title: 'User 7R Graph', cols: 4, rows: 4, color: '#4a4a4a', component: 'user-7r-graph', expand: false },
        { title: 'Client-ABC', cols: 4, rows: 2, color: '#4a4a4a', component: 'client-abc', expand: false },
    ];

    public selectedWidget: Widget<any> | null = null;

    // OE-265: the org dashboard's grid model (dashboard-layout.ts).
    private pageWidth = 0;
    private observer?: ResizeObserver;
    public gridColumns = 4;
    public rowHeight = 300;
    public editMode = false;

    /** The widgets as layout-grid sections, rebuilt only when the widgets or the legacy cell change. */
    private layoutCache: {
        widgets: InstantiatedWidget<any, any>[];
        cell: number;
        version: number;
        items: LayoutGridItem[];
    } | null = null;
    private layoutVersion = 0;

    /** Fine widths (twelfths) and heights (rows) offered in each card's menu. */
    public readonly widthOptions = [3, 4, 6, 8, 9, 12];
    public readonly heightOptions = [4, 6, 8, 10, 12, 16, 20];

    // Widget options.
    public allColumnWidths = [2, 4, 6, 8];
    public allRowsWidths = [2, 4, 6, 8];

    /**
     * Each 7R card's own date range, for its subtitle. One shared pair labelled every card with
     * whichever graph emitted last (the month card read "October 27 - November 27", OE-265).
     */
    private widgetDates = new WeakMap<InstantiatedWidget<any, any>, { from?: Date; to?: Date; width?: number }>();
    /** OE-265: graph card subtitles read on the same clock as the graphs, and follow a switch. */
    private readonly clock = inject(GraphClockService);
    private clockSub?: Subscription;

    /**
     * A boolean to force the re-calculation of widget's dimensions and update their scales.
     * Mainly used for the 7r graphs.
     */
    public refreshScale = false;

    //#endregion

    constructor(
        private host: ElementRef<HTMLElement>,
        private dialog: MatDialog,
        private zone: NgZone
    ) {}

    //#region Instantiation
    // Setup\fetches\initialization.

    public ngOnInit(): void {
        this.clockSub = this.clock.mode$.subscribe(() => {
            for (const widget of this.widgets || []) {
                const width = this.widgetDates.get(widget)?.width;
                if (width !== undefined) this.updateWidgetSubtitle(widget, width);
            }
        });
        // Columns and row height follow the dashboard's own width (side menu, window, phone).
        this.observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width || 0;
            if (!width) return;
            const columns = gridColumnsForWidth(width);
            const rowHeight = rowHeightFor(width, columns, GRID_GAP);
            this.pageWidth = width;
            if (columns !== this.gridColumns || rowHeight !== this.rowHeight) {
                this.zone.run(() => {
                    this.gridColumns = columns;
                    this.rowHeight = rowHeight;
                });
            }
        });
        this.observer.observe(this.host.nativeElement);
    }

    //#endregion
    //#region User Interactions
    // Callbacks from html, anything the user can "interact" with from the view.

    //#region Layout (OE-265)

    public get layoutItems(): LayoutGridItem[] {
        const c = this.layoutCache;
        if (c && c.widgets === this.widgets && c.cell === this.rowHeight && c.version === this.layoutVersion) {
            return c.items;
        }
        const items = this.widgets.map((w) => ({
            id: w.id,
            width: widthInTwelfths(w.settings),
            height: heightInRows(w.settings, this.rowHeight),
            minHeight: 4,
            minWidth: 360,
        }));
        this.layoutCache = { widgets: this.widgets, cell: this.rowHeight, version: this.layoutVersion, items };
        return items;
    }

    public widgetById(id: string): InstantiatedWidget<any, any> | undefined {
        return this.widgets.find((w) => w.id === id);
    }

    public onLayoutMoved(event: { from: number; to: number }): void {
        this.widgets = moveItem(this.widgets, event.from, event.to);
        this.updateWidgetsOrder.emit(this.widgets);
    }

    public onLayoutResized(event: { id: string; width: number; height: number }): void {
        const widget = this.widgetById(event.id);
        if (widget) this.setWidgetSize(widget, event.width, event.height);
    }

    /** Saves the fine size, and the old units roughly in step for anything that still reads them. */
    private setWidgetSize(widget: InstantiatedWidget<any, any>, width: number, height: number): void {
        widget.settings = { ...widget.settings, gridWidth: width, gridHeight: height, ...legacyUnits(width, height) };
        this.layoutVersion++;
        this.updateWidget.emit(widget);
    }

    //#endregion

    public openSettingsDialogForWidget(widget: InstantiatedWidget<any, any>) {
        const dialogRef = this.dialog.open(WidgetSettingsDialogComponent, {
            data: {
                allColumnWidths: this.allColumnWidths,
                allRowsWidths: this.allRowsWidths,
                filterBySalesCodes: [],
                integrations: this.integrations,
                organization: this.organization,
                currentUser: this.user as User,
                widget: widget,
            } as WidgetSettingsDialogInputData,
            width: '500px',
            height: '500px',
        });

        dialogRef.afterClosed().subscribe((newValues) => {
            if (newValues) {
                // Update local widget, emit new settings on the settingSub.
                if (newValues.settings) {
                    const previousRepKeys = widget.settings.repKeys;
                    const { gridWidth, gridHeight, columns, rows } = widget.settings;
                    const sizeKept = newValues.settings.columns === columns && newValues.settings.rows === rows;
                    widget.settings = sizeKept ? { ...newValues.settings, gridWidth, gridHeight } : newValues.settings;
                    this.layoutVersion++;
                    widget.settingsSub.next(newValues.settings);

                    if (widget.data.selectedRep && previousRepKeys !== widget.settings.repKeys) {
                        // If the widget is a clientAbc widget, then the repKeys is a string, not an array.
                        if (widget.type === 'clientAbc') {
                            widget.data.selectedRep.next(newValues.settings.repKeys[0]);
                        } else {
                            widget.data.selectedRep.next(newValues.settings.repKeys);
                        }
                    }

                    if (widget.data?.groupKeys && newValues.settings.groupKeys) {
                        widget.data.groupKeys.next(newValues.settings.groupKeys);
                    }
                }
                if (newValues.title) widget.title = newValues.title;
                if (newValues.auth) widget.auth = newValues.auth;

                this.updateWidget.emit(newValues);
            }
        });
    }

    public openAddWidgetDialog(): void {
        const ref = this.dialog.open<AddWidgetDialogComponent, AddWidgetDialogInputData>(AddWidgetDialogComponent, {
            // width: '800px',
            // height: '700px',
            data: {
                allColumnWidths: this.allColumnWidths,
                allRowsWidths: this.allRowsWidths,
                filterBySalesCodes: [],
                integrations: this.integrations,
                organization: this.organization,
                currentUser: this.user as User,
            },
        });

        ref.afterClosed().subscribe((result) => {
            if (result) {
                this.addNewWidget.emit(result);
            }
        });
    }

    public onDeleteWidget(widget: InstantiatedWidget<any, any>) {
        if (confirm('Are you sure you want to delete this widget?')) this.deleteWidget.emit(widget);
    }

    public expand(index: number) {
        // this.widgets[index].expand = true;
    }

    public collapse(index: number) {
        // this.widgets[index].expand = false;
    }

    public onClientAbcGroupExpand(event: { isExpanded: boolean; row: any }, widget: InstantiatedWidget<any, any>) {
        if (widget.type === 'clientAbcGroupList') {
            widget.data.expandFetchDataSubject.next(event);
        }
    }

    // public onClientAbcGroupEdit($event: ClientAbcGroupTableDatum) {
    //     throw new Error('Method not implemented.');
    // }

    // public onClientAbcGroupDelete($event: ClientAbcGroupTableDatum) {
    //     throw new Error('Method not implemented.');
    // }

    //#endregion
    //#region Events and Domain Functions
    // $watches/$on events, functions which don't fall into any of the other regions,
    // including any function that has to do domain functionality.

    private onColChange(widget: InstantiatedWidget<any, any>, twelfths: number): void {
        this.setWidgetSize(widget, twelfths, heightInRows(widget.settings, this.rowHeight));
    }

    private onRowChange(widget: InstantiatedWidget<any, any>, rows: number): void {
        this.setWidgetSize(widget, widthInTwelfths(widget.settings), rows);
    }

    public updateMenuOptions(widget: InstantiatedWidget<any, any>) {
        // Column and row options are present for all widgets.
        this.menuOptions = [
            {
                display: 'Width',
                icon: 'swap_horiz',
                subMenuItems: this.widthOptions.map((twelfths) => {
                    return {
                        selected: widthInTwelfths(widget.settings) === twelfths,
                        display: widthLabel(twelfths),
                        action: this.onColChange.bind(this, widget, twelfths),
                    };
                }),
                action: () => {
                    // Do nothing...
                },
            },
            {
                display: 'Height',
                icon: 'height',
                subMenuItems: this.heightOptions.map((rows) => {
                    return {
                        selected: heightInRows(widget.settings, this.rowHeight) === rows,
                        display: `${rows} rows tall`,
                        action: this.onRowChange.bind(this, widget, rows),
                    };
                }),
                action: () => {
                    // Do nothing...
                },
            },
        ];

        const settingsOpt: CustomMenuOptions<{ selected: boolean }> = {
            display: 'Settings',
            icon: 'settings',
            action: this.openSettingsDialogForWidget.bind(this, widget),
        };

        const deleteOpt: CustomMenuOptions<{ selected: boolean }> = {
            display: 'Delete Widget',
            colour: 'warn',
            icon: 'delete_forever',
            action: this.onDeleteWidget.bind(this, widget),
        };

        switch (widget.type) {
            case 'daily7r':
            case 'monthly7r':
                this.menuOptions.push(settingsOpt, deleteOpt);
                break;
            case 'clientAbc':
            case 'clientAbcGroup':
            case 'clientAbcGroupList':
                this.menuOptions.push(settingsOpt, deleteOpt);
                break;
            case 'clientAbcGraph':
                settingsOpt.disabled = true;

                this.menuOptions.push(settingsOpt, deleteOpt);
                break;
            default:
                console.error('IntermediateComponent.updateMenuOptions > type not recognized', widget.type);
                alert('Action failed - please try again.');
                break;
        }
    }

    public onWidgetDateChange(widget: InstantiatedWidget<any, any>, end: 'from' | 'to', date: Date): void {
        const dates = this.widgetDates.get(widget) || {};
        dates[end] = date;
        this.widgetDates.set(widget, dates);
        if (dates.from && dates.to) {
            setTimeout(() => {
                this.updateWidgetSubtitle(widget, this.pageWidth);
            });
        }
    }

    //#endregion
    //#region Data Access & Subscriptions
    // API interactions, NGRX selects, etc.

    public onWidgetResize(event: { width: number; height: number }, widget: InstantiatedWidget<any, any>) {
        // console.log('onWidgetResize', event, widget);

        setTimeout(() => {
            this.updateWidgetSubtitle(widget, event.width);
        });

        // console.log('onWidgetResize', event, widget);
        this.refreshScale = !this.refreshScale;
        console.log('onWidgetResize > refreshScale', this.refreshScale);
    }

    //#endregion
    //#region Setters, Updaters and Preloaders
    // Setter functions, and methods called from the Instantiation region.

    private updateWidgetSubtitle(widget: InstantiatedWidget<any, any>, containerWidth: number) {
        if (widget.type === 'daily7r' || widget.type === 'monthly7r') {
            const dates = this.widgetDates.get(widget) || {};
            dates.width = containerWidth;
            this.widgetDates.set(widget, dates);
            widget.subtitle = graphSubtitle(
                widget.type,
                this.clock.show(dates.from),
                this.clock.show(dates.to),
                containerWidth
            );
        } else if (widget.type === 'clientAbc') {
            if (this.user && widget.settings?.repKeys && this.user.organizations?.selected) {
                const selectedIntegration =
                    this.user.organizations[this.user.organizations.selected].selectedGatewayKey;

                if (selectedIntegration) {
                    const integrationReps = Object.values(
                        this.integrations?.find((i) => i.key === selectedIntegration)?.userMapping || {}
                    );
                    const repDoc = integrationReps.find((r) => r.key === widget.settings.repKeys[0]);
                    if (repDoc) {
                        widget.subtitle = repDoc.repName;
                    }
                }
            }
        }
    }

    //#endregion
    //#region Getters and Filters
    // Getter functions and filter methods/functions.

    //#endregion
    //#region Change Detection
    // Deep config comparisons, change log generation, hasChangesFn, etc.

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['dashboardKey'] && changes['dashboardKey'].currentValue) {
            // Replace dash with space, convert to title case.
            this.dashboardName = (changes['dashboardKey'].currentValue + '').replaceAll('-', ' ');
        }
    }

    //#endregion
    //#region Parsing and Validation
    // Transformation/mapping methods
    // Methods returning true/false: isCurrent, areFeedsEqual, shouldProcess, etc.

    //#endregion
    //#region Response Handlers
    // API/NGRX callback handlers.

    //#endregion
    //#region Navigation
    // Confirmation close dialogs, router guards, etc.

    public ngOnDestroy(): void {
        this.clockSub?.unsubscribe();
        this.observer?.disconnect();
    }

    //#endregion
}

interface CustomMenuOptions<T> extends DisplayActionIcon {
    subMenuItems?: (DisplayAction & T)[];
}
